import { useRef, useState } from 'preact/hooks'
import { SolaMark } from '~/components/identity/SolaMark'
import { Screen } from '~/components/layout/Screen'
import { Button } from '~/components/primitives/Button'
import { InfoButton } from '~/components/primitives/InfoButton'
import { confirmRestore, getRestoreState } from '~/exports/backup/restoreBackup'
import type { ValidationResult } from '~/exports/backup/validateBackup'
import { useAsyncData } from '~/hooks/useAsyncData'
import { DEFAULT_ROUTE, PLAN_ROUTE } from '~/routes'
import {
  APP_VERSION,
  createAndRecordBackup,
  deliverBackup,
  readFilesAsBytes,
  restoreFailureMessage,
  restoreValidatedBackup,
  validateBackupFiles,
} from '~/services/backupService'
import { previewReset, type ResetPreview, resetAllData } from '~/services/resetService'
import {
  type DeletionCategory,
  type DeletionPreview,
  deleteCategory,
  previewSelectiveDeletion,
} from '~/services/selectiveDeletionService'
import { getEditableRecords } from '~/services/settingsService'
import { useDatabase } from '~/storage/DatabaseContext'
import { estimateStorage, isPersisted } from '~/storage/quota'
import { getLastBackupAt } from '~/storage/repositories/metaRepository'
import { STORE } from '~/storage/schema'
import { DeletionSection } from './DeletionSection'
import { PhaseHistoryEditor } from './PhaseHistoryEditor'
import { ProjectEditor } from './ProjectEditor'
import { ResetSection } from './ResetSection'
import { RoutineAnchorEditor } from './RoutineAnchorEditor'
import { SettingsGroup } from './SettingsGroup'
import styles from './SettingsPage.module.css'
import { SetupEditor } from './SetupEditor'
import { WorkerConnectionEditor } from './WorkerConnectionEditor'

/**
 * Settings.
 *
 * Backup, replace-only restore and the visible last-backup date came from
 * Checkpoint 1.5. Editing what setup collected — profile, the active phase, the
 * goal, and display preferences — is added here, so onboarding is no longer a
 * one-way door.
 *
 * A full reset is here too (D-067) — pulled forward from Checkpoint 4 because
 * without it the only way to undo a bad first setup was to clear the browser's
 * site data, and weigh-ins cannot be deleted individually at all (D-035).
 *
 * **Selective deletion arrived at Checkpoint 4.3** — one kind of record at a
 * time, including "delete all photos", with the full reset still below it for
 * everything at once.
 *
 * Still absent, and named rather than stubbed: editing past phases and the
 * transition records between them (**L-003**).
 *
 * **There is no merge option, and none may be added** (D-018).
 */
export function SettingsPage() {
  const db = useDatabase()
  const fileInput = useRef<HTMLInputElement>(null)

  const [busy, setBusy] = useState<'none' | 'backup' | 'restore' | 'reset'>('none')
  const [message, setMessage] = useState<string | undefined>(undefined)
  const [problem, setProblem] = useState<string | undefined>(undefined)
  const [pending, setPending] = useState<ValidationResult | undefined>(undefined)
  /** Present only while the reset section is armed; also its armed flag. */
  const [resetPreview, setResetPreview] = useState<ResetPreview | undefined>(undefined)
  const [resetProblem, setResetProblem] = useState<string | undefined>(undefined)

  const [deletionPreview, setDeletionPreview] = useState<DeletionPreview | undefined>(undefined)
  const [deletionBusy, setDeletionBusy] = useState<DeletionCategory | undefined>(undefined)
  const [deletionProblem, setDeletionProblem] = useState<string | undefined>(undefined)
  const [deleted, setDeleted] = useState<{ category: DeletionCategory; count: number } | undefined>(
    undefined,
  )

  const { state, reload } = useAsyncData(async () => {
    const [lastBackup, persisted, estimate, restoreState, editable] = await Promise.all([
      getLastBackupAt(db),
      isPersisted(),
      estimateStorage(),
      getRestoreState(db),
      getEditableRecords(db, new Date()),
    ])
    return { lastBackup, persisted, estimate, restoreState, editable }
  }, [STORE.meta, STORE.profile, STORE.phases, STORE.goals, STORE.settings])

  async function runBackup() {
    setBusy('backup')
    setProblem(undefined)
    setMessage(undefined)

    const created = await createAndRecordBackup(db, new Date())
    if (!created.ok) {
      setBusy('none')
      setProblem(created.error.message)
      return
    }

    try {
      await deliverBackup(created.value.files)
      const parts = created.value.files.length
      setMessage(
        parts === 1
          ? 'Backup created. Keep the file somewhere it will survive losing this device.'
          : `Backup created in ${parts} parts. Keep every part together — restoring needs all of them.`,
      )
    } catch {
      setProblem('The backup was created but could not be saved. Try again.')
    }
    setBusy('none')
    reload()
  }

  async function chooseRestoreFile(files: FileList | null) {
    if (files === null || files.length === 0) return
    setProblem(undefined)
    setMessage(undefined)

    // Validation never mutates anything. The user sees what the backup holds
    // and confirms before the replacement happens (D-031).
    const parts = await readFilesAsBytes(Array.from(files))
    const validated = await validateBackupFiles(parts)

    if (!validated.ok) {
      setProblem(validated.error.message)
      setPending(undefined)
      return
    }
    setPending(validated)
  }

  async function applyRestore() {
    if (pending === undefined) return
    setBusy('restore')

    const restored = await restoreValidatedBackup(db, pending)
    setBusy('none')
    setPending(undefined)

    if (!restored.ok) {
      setProblem(restoreFailureMessage(restored.error))
      return
    }
    setMessage(
      `Restored ${restored.value.recordsRestored} records and ${restored.value.blobsRestored} files. Check that everything looks right, then confirm below.`,
    )
    reload()
  }

  async function openDeletion() {
    setDeletionProblem(undefined)
    const preview = await previewSelectiveDeletion(db)
    if (!preview.ok) {
      setProblem(preview.error.message)
      return
    }
    setDeletionPreview(preview.value)
  }

  async function applyDeletion(category: DeletionCategory) {
    setDeletionBusy(category)
    setDeletionProblem(undefined)
    setDeleted(undefined)

    const outcome = await deleteCategory(db, category)
    if (!outcome.ok) {
      setDeletionBusy(undefined)
      setDeletionProblem(outcome.error.message)
      return
    }

    /* Re-read rather than adjust the count in place. The screen states what is
       actually there now, which is the only figure worth showing next to a
       destructive action. */
    const refreshed = await previewSelectiveDeletion(db)
    setDeletionBusy(undefined)
    setDeleted({ category, count: outcome.value.recordsRemoved })
    if (refreshed.ok) setDeletionPreview(refreshed.value)
    reload()
  }

  async function armReset() {
    setResetProblem(undefined)
    const preview = await previewReset(db)
    if (!preview.ok) {
      setProblem(preview.error.message)
      return
    }
    setResetPreview(preview.value)
  }

  async function applyReset() {
    setBusy('reset')
    setResetProblem(undefined)

    const reset = await resetAllData(db)
    if (!reset.ok) {
      setBusy('none')
      setResetProblem(reset.error.message)
      return
    }

    // Reload rather than re-render. The onboarding gate is per-page and this
    // screen has none, so a re-render would leave the user on a Settings page
    // whose sections had silently vanished. A reload is also the only thing
    // that genuinely reproduces first run — boot against an empty database,
    // persistence re-requested, settings defaults re-materialised. Busy stays
    // set: a success message nobody stays to read would be dead code.
    window.location.hash = DEFAULT_ROUTE
    window.location.reload()
  }

  if (state.status !== 'ready') {
    return (
      <Screen title="Settings">
        <p>{state.status === 'error' ? state.message : 'Loading…'}</p>
      </Screen>
    )
  }

  const { lastBackup, persisted, estimate, restoreState, editable } = state.value
  const lastBackupAt = lastBackup.ok ? lastBackup.value : undefined
  const records = editable.ok ? editable.value : undefined

  return (
    <Screen title="Settings">
      {restoreState === 'awaiting-confirmation' ? (
        <section class={styles.confirmCard} aria-labelledby="confirm-heading">
          <h2 id="confirm-heading" class={styles.cardHeading}>
            Check your restored data
          </h2>
          <p>
            A copy of the data you had before the restore is still saved on this device. Once you
            confirm everything looks right, that copy can be removed.
          </p>
          <Button
            onClick={() => {
              void confirmRestore(db).then(reload)
            }}
          >
            Everything looks right
          </Button>
        </section>
      ) : null}

      {/* My plan is a link and nothing else. Duplicating the plan here is how
          two homes for the same thing start, and the junk drawer this grouping
          exists to fix was built one reasonable duplication at a time. */}
      <SettingsGroup id="group-plan" title="My plan" summary="What you are working on, and why.">
        <p class={styles.groupLede}>
          Your goals, the areas you chose and the reasoning behind them live on their own screen.
        </p>
        <Button
          variant="secondary"
          onClick={() => {
            window.location.hash = PLAN_ROUTE
          }}
        >
          Open my plan
        </Button>
      </SettingsGroup>

      <SettingsGroup
        id="group-goals"
        title="Goals and phase"
        summary="Your project, its phase, and the history of both."
      >
        {/* The project sits above the phase: a phase belongs to a project
            (D-062), and a reader meeting the phase decision first has no
            container to put it in. */}
        <ProjectEditor onSaved={reload} />
        {/* The phase and the goal are things the user is working towards, so they
            belong here rather than with height and units. `SetupEditor` renders a
            subset for exactly this reason. */}
        {records === undefined ? null : (
          <SetupEditor db={db} records={records} onSaved={reload} sections={['phase', 'goal']} />
        )}
        <PhaseHistoryEditor onSaved={reload} />
      </SettingsGroup>

      <SettingsGroup
        id="group-connections"
        title="Connections"
        summary="Other apps and services. Everything here is optional."
      >
        {/* Named rather than hidden (D-056). A group that appeared only once it
            had contents would make the six areas five, and the user would meet a
            new one on an update they did not ask for. */}
        <p class={styles.groupLede}>
          Project Sola works entirely on this device. Everything below is optional, off until you
          turn it on, and nothing here is needed for any recommendation the app makes. A wearable
          import is planned and does not exist yet.
        </p>
        <WorkerConnectionEditor />
      </SettingsGroup>

      <SettingsGroup
        id="group-reminders"
        title="Reminders"
        summary="When Project Sola brings something up."
      >
        <p class={styles.groupLede}>
          The backup notice appears after a review and again after fourteen days without a backup.
          Routine times below decide what Project Sola puts at the top of Today.
        </p>
        <RoutineAnchorEditor />
      </SettingsGroup>

      {/* Named for what is actually in it. "Display and measurement" alone would
          be a lie about the height and date of birth sitting beside the units, and
          a group whose title does not describe its contents is the junk drawer
          starting again one honest-sounding label at a time. */}
      <SettingsGroup
        id="group-display"
        title="About you and display"
        summary="Height, date of birth, units, and the measurement warning."
      >
        {records === undefined ? null : (
          <SetupEditor
            db={db}
            records={records}
            onSaved={reload}
            sections={['profile', 'preferences']}
          />
        )}
      </SettingsGroup>

      <SettingsGroup
        id="group-data"
        title="Data, privacy and backup"
        summary="Back up, restore, delete, and what is stored on this device."
        /**
         * The one group that starts open, and it was a browser test that showed
         * why. Today's backup reminder sends the user here to act — and with every
         * group collapsed they arrived at a screen with no backup control in
         * sight, having tapped a button that promised one. A collapse that hides
         * the destination of a prompt is not tidiness, it is a dead end.
         *
         * This is also the group holding the one operation that protects
         * everything else, so it is the right one to pay the open-by-default cost.
         * Five collapsed and one open is still a screen that can be scanned.
         */
        defaultOpen
      >
        <section aria-labelledby="backup-heading">
          <h2 id="backup-heading" class={styles.sectionHeading}>
            Backup
          </h2>

          <p class={styles.lastBackup}>
            {lastBackupAt === undefined ? (
              <>
                <strong>No backup yet.</strong> Local browser storage is not permanent — make one
                now.
              </>
            ) : (
              <>
                Last backup: <strong>{new Date(lastBackupAt).toLocaleString()}</strong>
              </>
            )}
          </p>

          {/* The claim stays, the enumeration moved (D-104 §5). "Everything,
              with no options" is the fact a user needs before pressing the
              button; the list of what "everything" contains is detail. */}
          <div class={styles.noteRow}>
            <p class={styles.note}>A backup contains everything, with nothing left out.</p>
            <InfoButton topic="what a backup contains" title="What a backup contains">
              <p>
                Your entries, measurements, photos and settings. There are no options to leave
                anything out — a partial backup is a restore that quietly loses something.
              </p>
              <p>
                The Worker access token is the one exception and it is deliberate: a backup restored
                onto another device does not carry your credentials with it.
              </p>
            </InfoButton>
          </div>

          {/* D-083. Said before the file is created, not after — a warning that
            arrives with the finished artifact is a notification, not a choice.
            Encryption was considered and rejected: a forgotten password turns
            the one thing that exists for the day everything else went wrong
            into total data loss. */}
          <p class={styles.note}>
            The file is <strong>not encrypted</strong>. Anyone who opens it can read everything in
            it, so keep it somewhere you trust.
          </p>

          <Button onClick={() => void runBackup()} busy={busy === 'backup'} fullWidth>
            Create backup
          </Button>
        </section>

        <section aria-labelledby="restore-heading">
          <h2 id="restore-heading" class={styles.sectionHeading}>
            Restore
          </h2>

          <p class={styles.warning}>
            Restoring <strong>replaces</strong> everything currently in Project Sola with the
            contents of the backup. It cannot be combined with your current data.
          </p>

          {/* Visually hidden but still in the accessibility tree, so it needs a
            name and must stay out of the tab order: the button below is the
            control, and a keyboard user reaching an unnamed file input first
            has no idea what it is for. Found by the 5.3 accessibility scan. */}
          <input
            ref={fileInput}
            type="file"
            accept=".zip,application/zip"
            multiple
            aria-label="Backup file to restore from"
            tabIndex={-1}
            class={styles.fileInput}
            onChange={(event) =>
              void chooseRestoreFile((event.currentTarget as HTMLInputElement).files)
            }
          />
          <Button variant="secondary" onClick={() => fileInput.current?.click()} fullWidth>
            Choose a backup file
          </Button>

          <RestorePreview
            pending={pending}
            busy={busy === 'restore'}
            onCancel={() => setPending(undefined)}
            onConfirm={() => void applyRestore()}
          />
        </section>

        <section aria-labelledby="storage-heading">
          <h2 id="storage-heading" class={styles.sectionHeading}>
            Storage on this device
          </h2>
          {/* D-033, treated exactly as setup's equivalent was at phase D: the
              claim is visible and the ways it can happen are one tap away. The
              user learns that local storage can be lost without opening
              anything. */}
          <div class={styles.noteRow}>
            <p class={styles.note}>
              Local browser storage is not guaranteed to last, and this device is the only copy.
            </p>
            <InfoButton topic="storage on this device" title="Storage on this device">
              <p>
                Clearing this browser&rsquo;s site data, removing the browser, losing the device, or
                the browser reclaiming space can destroy your data.
              </p>
              <p>
                Anyone who can use this unlocked phone or browser profile may be able to open
                Project Sola and see what is in it. There is no account and no password.
              </p>
            </InfoButton>
          </div>
          <dl class={styles.detail}>
            <div class={styles.row}>
              <dt>Persistent storage</dt>
              <dd>
                {persisted === 'granted'
                  ? 'Granted'
                  : persisted === 'denied'
                    ? 'Not granted by this browser'
                    : 'Not reported by this browser'}
              </dd>
            </div>
            {estimate.usageBytes === undefined ? null : (
              <div class={styles.row}>
                <dt>Used</dt>
                <dd>{(estimate.usageBytes / (1024 * 1024)).toFixed(1)} MB</dd>
              </div>
            )}
            {/* Which build is actually running. Worth showing on its own terms —
              a backup file records the app version that wrote it, so being able
              to read the running version makes a mismatch diagnosable instead
              of mysterious. It is also what makes an applied update visible. */}
            <div class={styles.row}>
              <dt>App version</dt>
              <dd>
                {APP_VERSION} ({__BUILD_ID__})
              </dd>
            </div>
          </dl>

          {/* A sign-off directly beneath the version it belongs to (D-089).
            Informational, not promotional: the mark, the name, and nothing else.
            It deliberately does not restate the version — that is the row above. */}
          <p class={styles.identity}>
            <SolaMark size="md" />
            <span class={styles.identityName}>Project Sola</span>
          </p>
        </section>

        <DeletionSection
          preview={deletionPreview}
          onOpen={() => void openDeletion()}
          onDelete={(category) => void applyDeletion(category)}
          busy={deletionBusy}
          problem={deletionProblem}
          removed={deleted}
        />
      </SettingsGroup>

      {/* Its own group, last, and the only one whose contents are irreversible.
          Grouping it with backup and restore would put "delete everything" one
          tap from "restore everything" — two controls whose outcomes are opposite
          and whose names both begin with the same idea of replacing what is
          there. */}
      <SettingsGroup
        id="group-advanced"
        title="Reset Project Sola"
        summary="Erase everything on this device. This cannot be undone."
      >
        <ResetSection
          preview={resetPreview}
          onArm={() => void armReset()}
          onCreateBackup={() => void runBackup()}
          backupBusy={busy === 'backup'}
          resetBusy={busy === 'reset'}
          problem={resetProblem}
          onCancel={() => {
            setResetPreview(undefined)
            setResetProblem(undefined)
          }}
          onConfirm={() => void applyReset()}
        />
      </SettingsGroup>

      {message === undefined ? null : (
        <p class={styles.success} role="status">
          {message}
        </p>
      )}
      {problem === undefined ? null : (
        <p class={styles.problem} role="alert">
          {problem}
        </p>
      )}
    </Screen>
  )
}

interface RestorePreviewProps {
  pending: ValidationResult | undefined
  busy: boolean
  onCancel: () => void
  onConfirm: () => void
}

/**
 * What a validated backup contains, before anything is replaced.
 *
 * Validation has already run and mutated nothing (D-031); this is the point at
 * which the user decides. Its own component because the replacement warning and
 * the two actions are the most consequential control in the app, and because
 * inlining it pushed the page past its complexity budget (D-056).
 */
function RestorePreview({ pending, busy, onCancel, onConfirm }: RestorePreviewProps) {
  if (pending?.ok !== true) return null

  const { manifest, migrated } = pending.backup
  const records = Object.values(manifest.recordCounts).reduce((sum, count) => sum + count, 0)

  return (
    <div class={styles.pendingCard}>
      <h3 class={styles.cardHeading}>This backup is valid</h3>
      <dl class={styles.detail}>
        <div class={styles.row}>
          <dt>Created</dt>
          <dd>{new Date(manifest.createdAt).toLocaleString()}</dd>
        </div>
        <div class={styles.row}>
          <dt>Records</dt>
          <dd>{records}</dd>
        </div>
        <div class={styles.row}>
          <dt>Photos and files</dt>
          <dd>{manifest.blobCount}</dd>
        </div>
      </dl>
      {migrated ? (
        <p class={styles.note}>
          This backup was made by an older version and will be brought up to date as it is restored.
        </p>
      ) : null}
      <p class={styles.warning}>
        Everything currently in Project Sola will be replaced. This cannot be undone from inside the
        app.
      </p>
      <div class={styles.actions}>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onConfirm} busy={busy}>
          Replace my data
        </Button>
      </div>
    </div>
  )
}
